import React from 'react';
import { Link } from 'react-router-dom';

const ContractorsLanding: React.FC = () => {
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-orange-600 to-red-700 text-white py-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            Contractors & Service Providers
                        </h1>
                        <p className="text-xl md:text-2xl text-orange-100 max-w-4xl mx-auto mb-8">
                            Expand your business with the NYC Local Law 97 retrofit boom. Connect with qualified building owners ready to invest.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link 
                                to="/contractor-partnership-signup" 
                                className="bg-white text-orange-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                            >
                                🤝 Join Our Network
                            </Link>
                            <Link 
                                to="/target-properties" 
                                className="bg-orange-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-orange-400 border-2 border-white transition-colors"
                            >
                                🎯 Find Target Properties
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Market Opportunity Section */}
            <div className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            The LL97 Market Opportunity
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            NYC's Local Law 97 creates massive demand for energy retrofit services
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
                        <div className="bg-gray-50 rounded-lg p-6">
                            <div className="text-3xl font-bold text-orange-600 mb-2">64,000+</div>
                            <div className="text-gray-600">Buildings Required to Comply</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-6">
                            <div className="text-3xl font-bold text-orange-600 mb-2">$2.5B+</div>
                            <div className="text-gray-600">Market Opportunity</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-6">
                            <div className="text-3xl font-bold text-orange-600 mb-2">2024</div>
                            <div className="text-gray-600">Fines Begin</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-6">
                            <div className="text-3xl font-bold text-orange-600 mb-2">85%</div>
                            <div className="text-gray-600">Need Retrofits</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Benefits Section */}
            <div className="py-16 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">
                                Why Partner With Us?
                            </h2>
                            <div className="space-y-6">
                                <div className="flex items-start space-x-4">
                                    <div className="text-orange-500 text-xl bg-orange-100 p-2 rounded-lg">🎯</div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Qualified Leads</h3>
                                        <p className="text-gray-600">
                                            Connect with building owners who have already identified their retrofit needs and are ready to invest.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <div className="text-orange-500 text-xl bg-orange-100 p-2 rounded-lg">📊</div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Data-Driven Targeting</h3>
                                        <p className="text-gray-600">
                                            Access comprehensive building energy data to identify the most promising opportunities in your service area.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <div className="text-orange-500 text-xl bg-orange-100 p-2 rounded-lg">🤝</div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Trusted Network</h3>
                                        <p className="text-gray-600">
                                            Build your reputation in our vetted contractor network and gain credibility with potential clients.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <div className="text-orange-500 text-xl bg-orange-100 p-2 rounded-lg">💼</div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Business Growth</h3>
                                        <p className="text-gray-600">
                                            Expand your portfolio with high-value LL97 compliance projects that provide recurring business opportunities.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Partnership Benefits</h3>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">Direct access to qualified building owners</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">Detailed building energy profiles</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">Project matching based on your expertise</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">Marketing support and promotion</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">Technical resources and support</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">No upfront fees or commitments</span>
                                </div>
                            </div>
                            <div className="mt-6">
                                <Link 
                                    to="/contractor-partnership-signup" 
                                    className="w-full bg-orange-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-orange-700 transition-colors block text-center"
                                >
                                    Join Our Network Today
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Service Categories */}
            <div className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            High-Demand Service Categories
                        </h2>
                        <p className="text-xl text-gray-600">
                            LL97 compliance creates demand across multiple specialty areas
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">🌡️</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">HVAC Systems</h3>
                            <p className="text-gray-600 mb-4">
                                High-efficiency heating, cooling, and ventilation system upgrades and installations.
                            </p>
                            <div className="text-sm text-gray-500">
                                High demand • Premium pricing • Recurring maintenance
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">🏠</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Building Envelope</h3>
                            <p className="text-gray-600 mb-4">
                                Insulation, windows, roofing, and air sealing to improve building energy efficiency.
                            </p>
                            <div className="text-sm text-gray-500">
                                Large projects • Good margins • Visible impact
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">💡</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Lighting & Controls</h3>
                            <p className="text-gray-600 mb-4">
                                LED lighting upgrades and smart building automation systems.
                            </p>
                            <div className="text-sm text-gray-500">
                                Quick payback • Volume opportunities • Repeat clients
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">⚡</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Electrical Systems</h3>
                            <p className="text-gray-600 mb-4">
                                Panel upgrades, wiring, and electrical infrastructure for energy efficiency measures.
                            </p>
                            <div className="text-sm text-gray-500">
                                Essential service • Licensed trade • Premium rates
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">📊</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Energy Auditing</h3>
                            <p className="text-gray-600 mb-4">
                                Comprehensive energy assessments and retro-commissioning services.
                            </p>
                            <div className="text-sm text-gray-500">
                                Entry point • Lead generator • Consulting opportunities
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">🔧</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Plumbing & Water</h3>
                            <p className="text-gray-600 mb-4">
                                Water heating system upgrades and plumbing efficiency improvements.
                            </p>
                            <div className="text-sm text-gray-500">
                                Steady demand • Trusted relationships • Emergency services
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tools for Contractors */}
            <div className="py-16 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Tools to Grow Your Business
                        </h2>
                        <p className="text-xl text-gray-600">
                            Access the data and insights you need to win more projects
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-2xl mb-4">🎯</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Target Properties Analysis</h3>
                            <p className="text-gray-600 mb-4">
                                Identify buildings in your service area that need retrofits and are likely to invest soon.
                            </p>
                            <Link 
                                to="/target-properties" 
                                className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium"
                            >
                                Explore Target Properties →
                            </Link>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-2xl mb-4">📋</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Building Analysis Tools</h3>
                            <p className="text-gray-600 mb-4">
                                Use our analysis tools to prepare detailed proposals and demonstrate your expertise to clients.
                            </p>
                            <Link 
                                to="/building-analysis" 
                                className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium"
                            >
                                Access Analysis Tools →
                            </Link>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-2xl mb-4">🔧</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Retrofit Calculator</h3>
                            <p className="text-gray-600 mb-4">
                                Calculate energy savings, costs, and payback periods for different retrofit measures.
                            </p>
                            <Link 
                                to="/retrofit-calculator" 
                                className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium"
                            >
                                Use Retrofit Calculator →
                            </Link>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-2xl mb-4">⚠️</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Non-Compliant Buildings</h3>
                            <p className="text-gray-600 mb-4">
                                Directory of buildings that are likely to face LL97 fines and need immediate attention.
                            </p>
                            <Link 
                                to="/non-compliant-buildings" 
                                className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium"
                            >
                                View Directory →
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Success Stories */}
            <div className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Contractor Success Stories
                        </h2>
                        <p className="text-xl text-gray-600">
                            See how contractors are growing their business with our platform
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-gray-50 rounded-lg p-6">
                            <div className="text-xl mb-4">🔥</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">HVAC Contractor - Queens</h3>
                            <p className="text-gray-600 mb-4">
                                "Landed 3 major HVAC retrofit projects worth $180k in my first month using the target properties tool."
                            </p>
                            <div className="text-sm text-gray-500">
                                — Mike's HVAC Solutions
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6">
                            <div className="text-xl mb-4">⚡</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Electrical Contractor - Manhattan</h3>
                            <p className="text-gray-600 mb-4">
                                "The building analysis data helps me prepare detailed proposals that win contracts. ROI was immediate."
                            </p>
                            <div className="text-sm text-gray-500">
                                — Empire Electric Services
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6">
                            <div className="text-xl mb-4">🏠</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">General Contractor - Brooklyn</h3>
                            <p className="text-gray-600 mb-4">
                                "Being in the verified network gives me credibility. Clients trust me more when they find me here."
                            </p>
                            <div className="text-sm text-gray-500">
                                — Brooklyn Building Solutions
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="py-16 bg-gradient-to-r from-orange-600 to-red-700 text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                        Join the LL97 Retrofit Network
                    </h2>
                    <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
                        Position your business at the forefront of NYC's energy transformation. Join hundreds of contractors already growing with LL97 opportunities.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link 
                            to="/contractor-partnership-signup" 
                            className="bg-white text-orange-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                        >
                            🤝 Join Our Network
                        </Link>
                        <Link 
                            to="/contact" 
                            className="bg-orange-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-orange-400 border-2 border-white transition-colors"
                        >
                            📞 Talk to Our Team
                        </Link>
                    </div>
                    <p className="text-sm text-orange-200 mt-6">
                        Free to join • No upfront costs • Immediate access to opportunities
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ContractorsLanding;
